import {
  buildKeditorSharedCssUrls,
  buildKeditorSharedJsUrls,
  buildTemplateCssUrls,
  buildTemplateJsUrls,
} from '../config/assetManifest';

/**
 * Inject <link rel="preload"> tags for every asset the canvas will load once
 * INIT_EDITOR arrives (KEditor/Froala base CSS+JS and template CSS/JS).
 *
 * Called synchronously at boot (main.ts), BEFORE the App chunk finished
 * downloading and before the parent handshake — so the ~50 asset downloads
 * overlap the app boot instead of starting only after INIT_EDITOR.
 *
 * The URLs are built from the same shared manifest KEditor.vue uses, from
 * query params the parent puts on the iframe src. Preloading only downloads;
 * execution still happens in dependency order in useEditorInitializationHandlers.
 * If the params are missing (old parent, standalone mode), this is a no-op
 * and the editor behaves exactly as before.
 *
 * Returns the number of injected preload links.
 */
export function preloadEditorAssets(search: string = window.location.search): number {
  const params = new URLSearchParams(search);
  if (params.get('mode') === 'form-builder') return 0;

  const assetBase = params.get('assetBase');
  const phpBase = params.get('phpBase');
  const collection = params.get('collection');
  const templateName = params.get('templateName');
  const theme = params.get('theme') || '';

  const cssUrls: string[] = [];
  const jsUrls: string[] = [];

  if (assetBase) {
    cssUrls.push(...buildKeditorSharedCssUrls(assetBase));
    jsUrls.push(...buildKeditorSharedJsUrls(assetBase));
  }
  if (phpBase && collection && templateName) {
    cssUrls.push(...buildTemplateCssUrls(phpBase, collection, templateName, theme));
    jsUrls.push(...buildTemplateJsUrls(phpBase, collection, templateName, theme));
  }

  const inject = (url: string, as: 'style' | 'script') => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.setAttribute('as', as);
    link.href = url;
    document.head.appendChild(link);
  };

  cssUrls.forEach((url) => inject(url, 'style'));
  jsUrls.forEach((url) => inject(url, 'script'));

  const count = cssUrls.length + jsUrls.length;
  if (count > 0) {
    console.log(`[Vue 3 Editor] Preloading ${count} canvas assets at boot`);
  }
  return count;
}
